import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Bot, MessageCircle, Send, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

const DEFAULT_CHAT_QUOTA = Object.freeze({
  consumedPrompt: false,
  promptsUsed: 0,
  maxPrompts: 10,
  remainingPrompts: 10,
  isLocked: false,
  lockedUntil: null,
  cooldownSecondsRemaining: 0,
})

const toInteger = (value, fallback) => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value)
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

const toBoolean = (value, fallback) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true
    if (value.toLowerCase() === 'false') return false
  }
  return fallback
}

const normalizeChatQuota = (value) => {
  if (!value || typeof value !== 'object') return null

  const maxPrompts = Math.max(toInteger(value.maxPrompts ?? value.max_prompts, 10), 1)
  const promptsUsed = Math.max(
    Math.min(toInteger(value.promptsUsed ?? value.prompts_used, 0), maxPrompts),
    0
  )
  const isLocked = toBoolean(value.isLocked ?? value.is_locked, false)
  const remainingPrompts = Math.max(
    Math.min(
      toInteger(
        value.remainingPrompts ?? value.remaining_prompts,
        isLocked ? 0 : Math.max(maxPrompts - promptsUsed, 0)
      ),
      maxPrompts
    ),
    0
  )
  const lockedUntilRaw = value.lockedUntil ?? value.locked_until

  return {
    consumedPrompt: toBoolean(value.consumedPrompt ?? value.consumed_prompt, false),
    promptsUsed,
    maxPrompts,
    remainingPrompts,
    isLocked,
    lockedUntil:
      typeof lockedUntilRaw === 'string' && lockedUntilRaw.trim().length > 0
        ? lockedUntilRaw
        : null,
    cooldownSecondsRemaining: Math.max(
      toInteger(value.cooldownSecondsRemaining ?? value.cooldown_seconds_remaining, 0),
      0
    ),
  }
}

const buildMessage = (role, content) => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  role,
  content,
})

const readInvokeErrorPayload = async (invokeError) => {
  if (invokeError?.context && typeof invokeError.context.json === 'function') {
    try {
      const payload = await invokeError.context.json()
      if (payload && typeof payload === 'object') return payload
    } catch {
      // no-op
    }
  }

  return null
}

const resolveInvokeErrorMessage = async (invokeError, t, preloadedPayload = null) => {
  const genericError = t('chat.error')
  const authError = t('chat.authError')

  if (!invokeError) return genericError

  if (invokeError instanceof Error && invokeError.message === 'missing-auth-session') {
    return authError
  }

  if (typeof invokeError?.message === 'string') {
    if (/jwt|authorization|unauthorized|unauthenticated|401/i.test(invokeError.message)) {
      return authError
    }
  }

  const payload = preloadedPayload || (await readInvokeErrorPayload(invokeError))
  if (payload?.code === 'chat_quota_locked') {
    return t('chat.lockedError')
  }

  const upstreamError = typeof payload?.error === 'string' ? payload.error : ''

  if (/jwt|authorization|unauthorized|unauthenticated|401/i.test(upstreamError)) {
    return authError
  }

  if (upstreamError) return upstreamError

  return genericError
}

const AIChatWidget = () => {
  const { t, i18n } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [draft, setDraft] = useState('')
  const [messages, setMessages] = useState([])
  const [error, setError] = useState('')
  const [quota, setQuota] = useState(DEFAULT_CHAT_QUOTA)
  const [isRefreshingQuota, setIsRefreshingQuota] = useState(false)
  const [showLockNotice, setShowLockNotice] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const lockNoticeTimeoutRef = useRef(null)

  const welcomeText = useMemo(() => t('chat.welcome'), [t])
  const typingText = t('chat.typing')
  const isLocked = quota.isLocked
  const quickOptions = [
    t('chat.quickOptions.profitIncrease'),
    t('chat.quickOptions.expenseReduction'),
    t('chat.quickOptions.lowProfitReason'),
    t('chat.quickOptions.businessGrowthFocus'),
  ]
  const hasUserMessages = useMemo(
    () => messages.some((message) => message.role === 'user'),
    [messages]
  )

  const showCooldownNotice = useCallback(() => {
    setShowLockNotice(true)
    if (lockNoticeTimeoutRef.current) {
      clearTimeout(lockNoticeTimeoutRef.current)
    }
    lockNoticeTimeoutRef.current = setTimeout(() => {
      setShowLockNotice(false)
    }, 3500)
  }, [])

  const refreshQuotaStatus = useCallback(async () => {
    setIsRefreshingQuota(true)

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('missing-auth-session')
      }

      const { data, error: invokeError } = await supabase.functions.invoke('interactive-llm-chat', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          mode: 'status',
          locale: i18n.language,
        },
      })

      if (invokeError) {
        throw invokeError
      }

      const resolvedQuota = normalizeChatQuota(data?.quota)
      if (resolvedQuota) {
        setQuota(resolvedQuota)
        return resolvedQuota
      }
    } catch (statusError) {
      console.error('Failed to fetch chat quota status:', statusError)
      if (statusError instanceof Error && statusError.message === 'missing-auth-session') {
        setError(t('chat.authError'))
      }
    } finally {
      setIsRefreshingQuota(false)
    }

    return null
  }, [i18n.language, t])

  useEffect(() => {
    if (!isLocked) return undefined

    const intervalId = setInterval(() => {
      refreshQuotaStatus()
    }, 60000)

    return () => clearInterval(intervalId)
  }, [isLocked, refreshQuotaStatus])

  useEffect(() => {
    if (!isLocked) return
    setIsOpen(false)
  }, [isLocked])

  useEffect(() => {
    if (!isOpen || messages.length > 0) return
    setMessages([buildMessage('assistant', welcomeText)])
  }, [isOpen, messages.length, welcomeText])

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1) return prev
      const [firstMessage] = prev
      if (firstMessage.role !== 'assistant') return prev
      if (firstMessage.content === welcomeText) return prev
      return [{ ...firstMessage, content: welcomeText }]
    })
  }, [welcomeText])

  useEffect(() => {
    if (!isOpen) return
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [isOpen, messages, isSending])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    inputRef.current?.focus()
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (lockNoticeTimeoutRef.current) {
        clearTimeout(lockNoticeTimeoutRef.current)
      }
    }
  }, [])

  const sendMessage = async (inputOverride = null) => {
    const userInput = (inputOverride ?? draft).trim()
    if (!userInput || isSending) return

    if (isLocked) {
      setIsOpen(false)
      showCooldownNotice()
      return
    }

    const historyForApi = messages
      .filter((message) => message.role === 'user' || message.role === 'assistant')
      .map(({ role, content }) => ({ role, content }))

    const userMessage = buildMessage('user', userInput)
    setMessages((prev) => [...prev, userMessage])
    if (!inputOverride) {
      setDraft('')
    }
    setError('')
    setIsSending(true)

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('missing-auth-session')
      }
      const { data, error: invokeError } = await supabase.functions.invoke('interactive-llm-chat', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          message: userInput,
          history: historyForApi,
          locale: i18n.language,
        },
      })

      if (invokeError) {
        throw invokeError
      }

      const resolvedQuota = normalizeChatQuota(data?.quota)
      if (resolvedQuota) {
        setQuota(resolvedQuota)
        if (resolvedQuota.isLocked) {
          setIsOpen(false)
          showCooldownNotice()
        }
      }

      const reply = typeof data?.reply === 'string' ? data.reply.trim() : ''
      if (!reply) {
        throw new Error('Empty AI response')
      }

      setMessages((prev) => [...prev, buildMessage('assistant', reply)])
    } catch (sendError) {
      console.error('Failed to send chat message:', sendError)
      const errorPayload = await readInvokeErrorPayload(sendError)
      const quotaFromError = normalizeChatQuota(errorPayload?.quota)
      const errorCode = typeof errorPayload?.code === 'string' ? errorPayload.code : ''

      if (quotaFromError) {
        setQuota(quotaFromError)
      }

      if (errorCode === 'chat_quota_locked' || quotaFromError?.isLocked) {
        setIsOpen(false)
        showCooldownNotice()
      }

      const resolvedError = await resolveInvokeErrorMessage(sendError, t, errorPayload)
      setError(resolvedError)
    } finally {
      setIsSending(false)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    await sendMessage()
  }

  const handleInputKeyDown = async (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      await sendMessage()
    }
  }

  const handleQuickOptionClick = async (optionText) => {
    await sendMessage(optionText)
  }

  const handleLauncherClick = async () => {
    setError('')

    if (isSending || isRefreshingQuota) return

    if (isOpen) {
      setIsOpen(false)
      return
    }

    const latestQuota = await refreshQuotaStatus()
    const effectiveLockedState = latestQuota ? latestQuota.isLocked : isLocked

    if (effectiveLockedState) {
      setIsOpen(false)
      showCooldownNotice()
      return
    }

    setIsOpen(true)
  }

  return (
    <div className="fixed bottom-6 right-4 z-40 flex items-end justify-end sm:right-6">
      <div className="flex flex-col items-end gap-3">
        {showLockNotice && isLocked && (
          <div
            className="max-w-[22.5rem] rounded-xl border border-[#FFD1DD] bg-white px-3 py-2 text-left shadow-[0_12px_24px_rgba(17,24,39,0.2)]"
            role="status"
            aria-live="polite"
          >
            <p className="text-xs font-semibold text-[#FF1B54]">{t('chat.lockedWaitMessage')}</p>
            <p className="mt-1 text-[11px] text-gray-600">{t('chat.lockedSubtitle')}</p>
          </div>
        )}

        {isOpen && (
          <section
            className="w-[22.5rem] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-[#FFD1DD] bg-white shadow-[0_20px_45px_rgba(17,24,39,0.25)]"
            role="dialog"
            aria-modal="false"
            aria-label={t('chat.title')}
          >
            <header className="flex items-center justify-between bg-gradient-to-r from-[#FF1B54] via-[#C72D8F] to-[#36B9D4] px-4 py-3 text-white">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/40">
                  <Bot className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold leading-tight">{t('chat.title')}</h2>
                  <p className="text-xs text-white/90">
                    {isLocked ? t('chat.lockedSubtitle') : t('chat.subtitle')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-md p-1 text-white/90 transition-colors hover:bg-white/20 hover:text-white"
                aria-label={t('chat.close')}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>

            <div className="h-[22rem] overflow-y-auto bg-gray-50 px-3 py-4">
              <div className="space-y-3">
                {messages.map((message) => {
                  const isUser = message.role === 'user'
                  return (
                    <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                          isUser
                            ? 'rounded-br-md bg-[#FF1B54] text-white'
                            : 'rounded-bl-md border border-[#FFD1DD] bg-white text-gray-800'
                        }`}
                      >
                        {message.content}
                      </div>
                    </div>
                  )
                })}

                {!hasUserMessages && (
                  <div className="pt-1">
                    <p className="mb-2 px-1 text-xs font-medium text-gray-500">
                      {t('chat.quickOptions.label')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {quickOptions.map((optionText, optionIndex) => (
                        <button
                          key={`${optionIndex}-${optionText}`}
                          type="button"
                          onClick={() => handleQuickOptionClick(optionText)}
                          disabled={isSending || isLocked}
                          className="rounded-full border border-[#7ED8EA] bg-white px-3 py-2 text-xs font-medium text-gray-800 transition-colors hover:bg-[#F1FAFD] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {optionText}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isSending && (
                  <div className="flex justify-start">
                    <div className="max-w-[85%] rounded-2xl rounded-bl-md border border-[#FFD1DD] bg-white px-3.5 py-2.5 text-sm text-gray-600 shadow-sm">
                      {typingText}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="border-t border-gray-200 bg-white p-3">
              {error && <p className="mb-2 text-xs text-red-600">{error}</p>}

              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder={t('chat.placeholder')}
                  rows={1}
                  className="max-h-28 min-h-[2.75rem] flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#FF1B54] focus:outline-none focus:ring-2 focus:ring-[#FF1B54]/30"
                  disabled={isSending || isLocked}
                />
                <button
                  type="submit"
                  disabled={isSending || isLocked || !draft.trim()}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-r from-[#FF1B54] to-[#E1174C] text-white shadow-sm transition-all hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label={isSending ? t('chat.sending') : t('chat.send')}
                >
                  <Send className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </form>
          </section>
        )}

        <button
          type="button"
          onClick={handleLauncherClick}
          className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-[#FF1B54] to-[#E1174C] text-white shadow-[0_10px_25px_rgba(255,27,84,0.4)] transition-all hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#FF1B54]/30"
          aria-label={
            isLocked
              ? t('chat.lockedWaitMessage')
              : isOpen
                ? t('chat.close')
                : t('chat.open')
          }
          title={
            isLocked
              ? t('chat.lockedWaitMessage')
              : isOpen
                ? t('chat.close')
                : t('chat.open')
          }
        >
          <MessageCircle className="h-7 w-7" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

export default AIChatWidget
